/**
 * BANNER CALCULATOR — Jun 2026 (V1, aislado).
 * Banner 13 oz único material. Medidas con unidades independientes (in/ft/cm),
 * múltiples tamaños, tier ÚNICO por ft² terminados combinados, mínimo $30/job,
 * extras POR LÍNEA (Grommets $0 default Sí · Reinforced +$15/banner ·
 * Lamination +$1/ft² terminado), rollo útil 53", rotación automática, nesting
 * de múltiples piezas lado a lado, panelización automática con overlap 1".
 * Production Area = consumo real de rollo (largo consumido × 53").
 * Costo interno $1.00/ft² de producción (sin waste adicional).
 * Emite UN EstimateItem (quantity=1, unit_price=total) con metadata `banner_calc`.
 * Autocontenido: NO comparte código con VinylCalculatorModal (congelada) ni backend.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

const USABLE_IN = 53;
const OVERLAP_IN = 1;
const MIN_CHARGE = 30;
const LAMINATION_RATE = 1.0;
const REINFORCED_EACH = 15;
// Bloque 1 Partner Points — costo interno fuera del bundle (runtime fetch).
const BANNER_TIERS = [
  { max: 9, rate: 6.25, label: "1–9 ft²" },
  { max: 24, rate: 5.5, label: "10–24 ft²" },
  { max: 49, rate: 5.25, label: "25–49 ft²" },
  { max: 99, rate: 5.0, label: "50–99 ft²" },
  { max: Infinity, rate: 4.75, label: "100+ ft²" },
];
const UNITS = ["in", "ft", "cm"];

export function toFeet(value, unit) {
  const v = Number(value);
  if (unit === "in") return v / 12;
  if (unit === "cm") return v / 30.48;
  return v;
}

// Layout por línea: evalúa ambas orientaciones. Si la pieza cabe (cross ≤ 53)
// anida floor(53/cross) piezas lado a lado; si no cabe en ninguna, paneliza
// (overlap 1", sin nesting entre paneles). Preferencia: sin paneles → menor
// production area → menor largo de rollo. Production = largo consumido × 53".
export function planBannerLayout(wIn, hIn, qty) {
  const opts = [
    { cross: wIn, along: hIn, orientation: "normal" },
    { cross: hIn, along: wIn, orientation: "rotated" },
  ].map((o) => {
    if (o.cross <= USABLE_IN) {
      const across = Math.max(1, Math.floor(USABLE_IN / o.cross));
      const rows = Math.ceil(qty / across);
      const lengthIn = rows * o.along;
      return { ...o, panels: 1, across, rows, lengthIn, prodFt2: (lengthIn * USABLE_IN) / 144 };
    }
    const panels = Math.ceil((o.cross - USABLE_IN) / (USABLE_IN - OVERLAP_IN)) + 1;
    const lengthIn = qty * panels * o.along;
    return { ...o, panels, across: 1, rows: qty, lengthIn, prodFt2: (lengthIn * USABLE_IN) / 144 };
  });
  opts.sort(
    (a, b) => a.panels - b.panels || a.prodFt2 - b.prodFt2 || a.lengthIn - b.lengthIn
  );
  return opts[0];
}

export function computeBannerJob(sizes, internalCostRate = 0) {
  let finished = 0;
  let production = 0;
  let lamTotal = 0;
  let reinfTotal = 0;
  const lines = sizes.map((s) => {
    const qty = Number(s.qty);
    const wFt = toFeet(s.w, s.wu);
    const hFt = toFeet(s.h, s.hu);
    const layout = planBannerLayout(wFt * 12, hFt * 12, qty);
    const areaPer = wFt * hFt;
    const lineFinished = areaPer * qty;
    finished += lineFinished;
    production += layout.prodFt2;
    const lineLam = s.lamination ? lineFinished * LAMINATION_RATE : 0;
    const lineReinf = s.reinforced ? qty * REINFORCED_EACH : 0;
    lamTotal += lineLam;
    reinfTotal += lineReinf;
    return {
      quantity: qty,
      original_width: Number(s.w),
      original_width_unit: s.wu,
      original_height: Number(s.h),
      original_height_unit: s.hu,
      converted_width_ft: wFt,
      converted_height_ft: hFt,
      area_per_banner: areaPer,
      line_finished_area: lineFinished,
      line_production_area: layout.prodFt2,
      grommets: !!s.grommets,
      reinforced: !!s.reinforced,
      lamination: !!s.lamination,
      lamination_charge: Math.round(lineLam * 100) / 100,
      reinforcement_charge: lineReinf,
      orientation: layout.orientation,
      pieces_across_roll: layout.across,
      rows: layout.rows,
      paneling_required: layout.panels > 1,
      panel_count: layout.panels,
      overlap_inches: layout.panels > 1 ? OVERLAP_IN : 0,
      usable_roll_width: USABLE_IN,
    };
  });
  const tier = BANNER_TIERS.find((t) => finished <= t.max);
  const basePrice = finished * tier.rate;
  const baseCharge = Math.max(basePrice, MIN_CHARGE);
  const total = Math.round((baseCharge + lamTotal + reinfTotal) * 100) / 100;
  const internalCost = Math.round(production * internalCostRate * 100) / 100;
  return {
    lines,
    total_finished_area: finished,
    total_production_area: production,
    pricing_tier: tier.label,
    base_rate_per_sqft: tier.rate,
    calculated_base_price: Math.round(basePrice * 100) / 100,
    minimum_applied: basePrice < MIN_CHARGE,
    base_banner_charge: Math.round(baseCharge * 100) / 100,
    lamination_total: Math.round(lamTotal * 100) / 100,
    reinforcement_total: reinfTotal,
    final_price: total,
    internal_material_cost: internalCost,
  };
}

const fmt = (v, d = 2) =>
  Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
const money = (v) => `$${fmt(v)}`;
const emptySize = () => ({
  qty: "1", w: "", wu: "in", h: "", hu: "in",
  grommets: true, reinforced: false, lamination: false,
});
const sizeValid = (s) => {
  const q = Number(s.qty);
  const w = Number(s.w);
  const h = Number(s.h);
  return (
    Number.isFinite(q) && q > 0 &&
    Number.isFinite(w) && w > 0 &&
    Number.isFinite(h) && h > 0 &&
    UNITS.includes(s.wu) && UNITS.includes(s.hu)
  );
};

export default function BannerCalculatorModal({ onClose, onAdd }) {
  const { t } = useI18n();
  const [sizes, setSizes] = useState([emptySize()]);
  const [internalCostRate, setInternalCostRate] = useState(0);
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/internal-costs`, { withCredentials: true })
      .then(({ data }) => {
        if (!cancelled && data?.banner) setInternalCostRate(data.banner.internal_cost_rate || 0);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const allValid = sizes.length > 0 && sizes.every(sizeValid);
  const calc = useMemo(
    () => (allValid ? computeBannerJob(sizes, internalCostRate) : null),
    [allValid, sizes, internalCostRate]
  );
  const setSize = (i, patch) =>
    setSizes((arr) => arr.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const handleAdd = () => {
    if (!calc) {
      toast.error(t("banner_invalid"));
      return;
    }
    const customerNotes = sizes
      .map((s) => {
        const tags = [
          s.grommets && "Grommets",
          s.reinforced && "Reinforced",
          s.lamination && "Laminated",
        ].filter(Boolean);
        return `${Number(s.qty)} ${Number(s.qty) === 1 ? "pc" : "pcs"} — ${s.w} ${s.wu} × ${s.h} ${s.hu}${tags.length ? " — " + tags.join(" — ") : ""}`;
      })
      .join("\n");
    const prodLines = calc.lines
      .map((l, i) => {
        const nest =
          l.pieces_across_roll > 1
            ? `${l.pieces_across_roll} across × ${l.rows} filas`
            : l.paneling_required
            ? `${l.panel_count} paneles (overlap 1")`
            : `1 across × ${l.rows} filas`;
        return (
          `#${i + 1}: ${l.quantity} ${l.quantity === 1 ? "pc" : "pcs"} — ` +
          `${l.original_width} ${l.original_width_unit} × ${l.original_height} ${l.original_height_unit} ` +
          `→ ${fmt(l.converted_width_ft)} ft × ${fmt(l.converted_height_ft)} ft | ` +
          `${l.grommets ? "Grommets: Sí" : "⚠ NO GROMMETS"} | Reinforced: ${l.reinforced ? "Sí" : "No"} | ` +
          `Lamination: ${l.lamination ? "Sí" : "No"} | ${nest} (${l.orientation})`
        );
      })
      .join("\n");
    const internalNotes =
      `[BANNER 13 OZ] Rollo útil: ${USABLE_IN} in | Overlap paneles: ${OVERLAP_IN} in\n` +
      `${prodLines}\n` +
      `Finished: ${fmt(calc.total_finished_area)} ft² | Production: ${fmt(calc.total_production_area)} ft² | ` +
      `Tier: ${calc.pricing_tier} @ $${fmt(calc.base_rate_per_sqft)}/ft²`;
    onAdd({
      description: "Banner 13 oz",
      quantity: 1,
      unit_price: calc.final_price,
      unit_cost: calc.internal_material_cost,
      taxable: true,
      source_type: "banner_calculator",
      manual_price_override: true,
      price_manual: true,
      customer_notes: customerNotes,
      internal_notes: internalNotes,
      notes: internalNotes,
      banner_calc: {
        product: "Banner 13 oz",
        total_finished_area: calc.total_finished_area,
        total_production_area: calc.total_production_area,
        pricing_tier: calc.pricing_tier,
        base_rate_per_sqft: calc.base_rate_per_sqft,
        calculated_base_price: calc.calculated_base_price,
        minimum_applied: calc.minimum_applied,
        base_banner_charge: calc.base_banner_charge,
        lamination_total: calc.lamination_total,
        reinforcement_total: calc.reinforcement_total,
        final_price: calc.final_price,
        internal_material_cost: calc.internal_material_cost,
        usable_roll_width: USABLE_IN,
        overlap_inches: OVERLAP_IN,
        sizes: calc.lines,
      },
    });
    onClose();
  };

  return (
    <div
      data-testid="banner-calc-overlay"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        data-testid="banner-calc-modal"
        className="bg-white w-full max-w-2xl max-h-[92vh] overflow-auto border border-black shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-black flex items-start justify-between gap-3">
          <div>
            <div className="label-overline">GP / BANNER 13 OZ</div>
            <h2 className="font-display text-2xl font-black tracking-tight mt-1">
              {t("banner_calc_title")}
            </h2>
          </div>
          <button
            type="button"
            data-testid="banner-calc-close"
            onClick={onClose}
            className="font-mono-tech text-xs font-bold px-3 py-2 border border-black hover:bg-black hover:text-white"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="space-y-3">
            {sizes.map((s, i) => (
              <div
                key={i}
                data-testid={`banner-size-row-${i}`}
                className="border border-[var(--border)] p-3 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.2em] text-[var(--text-secondary)]">
                    {t("banner_size").toUpperCase()} #{i + 1}
                  </div>
                  {sizes.length > 1 && (
                    <button
                      type="button"
                      data-testid={`banner-size-remove-${i}`}
                      onClick={() => setSizes((arr) => arr.filter((_, x) => x !== i))}
                      className="text-xs text-[var(--alert-overdue,#c00)] hover:underline"
                    >
                      {t("banner_remove_size")}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
                  <label className="block">
                    <div className="text-[0.6rem] font-mono-tech font-bold text-[var(--text-secondary)] mb-1">
                      {t("banner_qty").toUpperCase()}
                    </div>
                    <input
                      type="number" min="1" step="1" value={s.qty}
                      onChange={(e) => setSize(i, { qty: e.target.value })}
                      data-testid={`banner-size-qty-${i}`}
                      className="w-full border border-[var(--border)] px-2 py-1.5 text-sm text-right"
                    />
                  </label>
                  <label className="block">
                    <div className="text-[0.6rem] font-mono-tech font-bold text-[var(--text-secondary)] mb-1">
                      {t("banner_width").toUpperCase()}
                    </div>
                    <input
                      type="number" min="0" step="any" value={s.w}
                      onChange={(e) => setSize(i, { w: e.target.value })}
                      data-testid={`banner-size-width-${i}`}
                      className="w-full border border-[var(--border)] px-2 py-1.5 text-sm text-right"
                    />
                  </label>
                  <select
                    value={s.wu}
                    onChange={(e) => setSize(i, { wu: e.target.value })}
                    data-testid={`banner-size-width-unit-${i}`}
                    className="border border-[var(--border)] px-2 py-1.5 text-sm"
                  >
                    {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                  <label className="block">
                    <div className="text-[0.6rem] font-mono-tech font-bold text-[var(--text-secondary)] mb-1">
                      {t("banner_height").toUpperCase()}
                    </div>
                    <input
                      type="number" min="0" step="any" value={s.h}
                      onChange={(e) => setSize(i, { h: e.target.value })}
                      data-testid={`banner-size-height-${i}`}
                      className="w-full border border-[var(--border)] px-2 py-1.5 text-sm text-right"
                    />
                  </label>
                  <select
                    value={s.hu}
                    onChange={(e) => setSize(i, { hu: e.target.value })}
                    data-testid={`banner-size-height-unit-${i}`}
                    className="border border-[var(--border)] px-2 py-1.5 text-sm"
                  >
                    {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div className="flex flex-wrap gap-4 text-sm pt-1">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox" checked={s.grommets}
                      onChange={(e) => setSize(i, { grommets: e.target.checked })}
                      data-testid={`banner-grommets-${i}`}
                    />
                    Grommets ($0)
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox" checked={s.reinforced}
                      onChange={(e) => setSize(i, { reinforced: e.target.checked })}
                      data-testid={`banner-reinforced-${i}`}
                    />
                    Reinforced (+$15 c/u)
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox" checked={s.lamination}
                      onChange={(e) => setSize(i, { lamination: e.target.checked })}
                      data-testid={`banner-lamination-${i}`}
                    />
                    Lamination (+$1.00/ft²)
                  </label>
                </div>
              </div>
            ))}
            <button
              type="button"
              data-testid="banner-add-size"
              onClick={() => setSizes((arr) => [...arr, emptySize()])}
              className="font-mono-tech text-xs font-bold px-3 py-2 border border-black hover:bg-black hover:text-white"
            >
              {t("banner_add_size")}
            </button>
          </div>

          {calc && (
            <div
              data-testid="banner-summary"
              className="border-l-4 border-black bg-[var(--bg,#fafafa)] px-4 py-3 space-y-1 text-sm"
            >
              <div className="flex justify-between">
                <span>{t("banner_finished_area")}</span>
                <span className="font-mono-tech tabular-nums font-bold" data-testid="banner-finished-area">
                  {fmt(calc.total_finished_area)} ft²
                </span>
              </div>
              <div className="flex justify-between">
                <span>{t("banner_production_area")}</span>
                <span className="font-mono-tech tabular-nums" data-testid="banner-production-area">
                  {fmt(calc.total_production_area)} ft²
                </span>
              </div>
              <div className="flex justify-between">
                <span>{t("banner_tier")}</span>
                <span className="font-mono-tech" data-testid="banner-tier">
                  {calc.pricing_tier} · ${fmt(calc.base_rate_per_sqft)}/ft²
                </span>
              </div>
              <div className="flex justify-between">
                <span>
                  {t("banner_base_charge")}
                  {calc.minimum_applied && (
                    <span className="ml-1 text-xs text-[var(--text-secondary)]" data-testid="banner-min-applied">
                      ({t("banner_min_applied")})
                    </span>
                  )}
                </span>
                <span className="font-mono-tech tabular-nums" data-testid="banner-base-charge">
                  {money(calc.base_banner_charge)}
                </span>
              </div>
              {calc.lamination_total > 0 && (
                <div className="flex justify-between">
                  <span>Lamination</span>
                  <span className="font-mono-tech tabular-nums" data-testid="banner-lamination-charge">
                    {money(calc.lamination_total)}
                  </span>
                </div>
              )}
              {calc.reinforcement_total > 0 && (
                <div className="flex justify-between">
                  <span>Reinforced</span>
                  <span className="font-mono-tech tabular-nums" data-testid="banner-reinforced-charge">
                    {money(calc.reinforcement_total)}
                  </span>
                </div>
              )}
              <div className="flex justify-between border-t border-[var(--border)] pt-1 font-bold">
                <span>{t("banner_total")}</span>
                <span className="font-mono-tech tabular-nums" data-testid="banner-total">
                  {money(calc.final_price)}
                </span>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              data-testid="banner-cancel"
              onClick={onClose}
              className="font-mono-tech text-xs font-bold px-4 py-2 border border-black hover:bg-black hover:text-white"
            >
              {t("banner_cancel")}
            </button>
            <button
              type="button"
              data-testid="banner-add-to-estimate"
              onClick={handleAdd}
              disabled={!calc}
              className="font-mono-tech text-xs font-bold px-4 py-2 bg-black text-white border border-black hover:bg-[var(--primary,#e5007d)] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t("banner_add_to_estimate")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
